/**
 * staffCapacityModel.ts
 * Pure, dependency-light helpers for the "Staff this job" v2 capacity surface
 * (BUG-2026-0144 — Gigi's feedback). Two concerns, both testable in isolation:
 *
 *   1. WEEK HEADERS — turn the plan's Saturday week-starts into labelled
 *      columns ("WEEK 1" + the Mon–Fri date range) so the capacity table reads
 *      as a clean week-columned grid (feedback #1 + #3).
 *   2. WEEK-LOAD coupling — summarize a person's per-week job-allocation % over
 *      the weeks a task runs, so each task card can show that person's weekly
 *      load right beside their chip (feedback #2). This is DISPLAY of the
 *      existing count-once per-person-per-week job share — never a per-task
 *      number that sums.
 *
 * No React / MUI / theme — the components choose the token colors from the band.
 */

import { fmtWeekRange } from '../../../../utils/weekUtils';
import { allocationBand, type AllocationBand } from '../../../../utils/scheduleAllocationBands';

/**
 * Staff-this-job redesign (2026-07-14) — how many BST weeks of read-only
 * "beyond project" availability context to show past the last task week
 * (SPEC.md §2: "so a scheduler can see where a busy person frees up").
 */
export const FUTURE_HORIZON_WEEKS = 5;

/** One labelled week column of the capacity table. */
export interface WeekHeader {
  /** BST (Saturday) week-start key. */
  weekStart: string;
  /** 1-based position in the plan span. */
  index: number;
  /** "WEEK 1", "WEEK 2", … */
  numberLabel: string;
  /** Mon–Fri date range, e.g. "Jul 13 – Jul 17". */
  rangeLabel: string;
}

/** Map ordered week-starts → labelled columns (number + date range). */
export function buildWeekHeaders(weekColumns: readonly string[]): WeekHeader[] {
  return weekColumns.map((weekStart, i) => ({
    weekStart,
    index: i + 1,
    numberLabel: `WEEK ${i + 1}`,
    rangeLabel: fmtWeekRange(weekStart),
  }));
}

/** A person's job-allocation % for one week. */
export interface WeekLoad {
  weekStart: string;
  percent: number;
}

/** A compact summary of a person's weekly job load over a task's run weeks. */
export interface WeekLoadSummary {
  /** True when the person has a non-zero job share in at least one of the weeks. */
  hasAny: boolean;
  /** Lowest non-zero % across the weeks (0 when none). */
  min: number;
  /** Highest % across the weeks (0 when none). */
  max: number;
  /** "100%" (uniform) / "50–100%" (varies) / "—" (no share set). */
  label: string;
  /** Band of the HIGHEST week (drives the coupled chip's color). */
  band: AllocationBand;
}

/**
 * Summarize a person's weekly job share over a task's run weeks. Count-once:
 * each entry is the person's job-week share for THAT week (never summed across
 * tasks). A single week collapses to "100%"; a varying span reads "50–100%".
 */
export function summarizeWeekLoad(loads: readonly WeekLoad[]): WeekLoadSummary {
  const pos = loads.map((l) => Math.round(l.percent || 0)).filter((p) => p > 0);
  if (pos.length === 0) {
    return { hasAny: false, min: 0, max: 0, label: '—', band: 'none' };
  }
  const min = Math.min(...pos);
  const max = Math.max(...pos);
  const label = min === max ? `${max}%` : `${min}–${max}%`;
  return { hasAny: true, min, max, label, band: allocationBand(max) };
}

/**
 * ASSIGN → set-% decision (BUG-2026-0141 #1). When a person is assigned to a
 * task card we FILL their count-once source share to 100 for the weeks the task
 * runs — but ONLY the weeks where they have no share yet (0). Weeks where they
 * already carry a deliberate part-time share (e.g. a mentor's 25%) are left
 * untouched, so assigning can never leave "present-but-0%" and never clobbers an
 * intentional allocation. Returns the subset of `weeks` to write @100.
 */
export function weeksToFillOnAssign(
  weeks: readonly string[],
  currentPercentFor: (weekStart: string) => number,
): string[] {
  return weeks.filter((w) => (currentPercentFor(w) || 0) <= 0);
}

/**
 * THIS-JOB %-to-zero decision (BUG-2026-0141 #1). Dialing a PRESENT person's
 * this-job % down to 0 means "take them off this job that week" (confirm, then
 * remove) — not "present at 0%". Only true when they currently have a share; a
 * cell already at 0 (nothing to remove) returns false.
 */
export function isRemoveFromJobIntent(nextPercent: number, currentPercent: number): boolean {
  return nextPercent <= 0 && (currentPercent || 0) > 0;
}
